// Importar todos los JSON de documentación de APIs
import archivosDoc from './docs/archivos.json';
import binomialesDoc from './docs/binomiales.json';
import docsDoc from './docs/docs.json';
import estadisticasDoc from './docs/estadisticas.json';
import fechasDoc from './docs/fechas.json';
import fisicasDoc from './docs/fisicas.json';
import gaussDoc from './docs/gauss.json';
import graficosDoc from './docs/graficos.json';
import imagenesDoc from './docs/imagenes.json';
import markovDoc from './docs/markov.json';
import matricesDoc from './docs/matrices.json';
import mergeDoc from './docs/merge.json';
import neuralDoc from './docs/neural.json';
import numericosDoc from './docs/numericos.json';
import openIaDoc from './docs/open_ia.json';
import ordenarTablasDoc from './docs/ordenar_tablas.json';
import poissonDoc from './docs/poisson.json';
import simplex2phDoc from './docs/simplex2ph.json';
import sqliteDoc from './docs/sqlite.json';
import stringsDoc from './docs/strings.json';
import tablasDoc from './docs/tablas.json';
import vectoresDoc from './docs/vectores.json';
import websDoc from './docs/webs.json';

// Lista de todos los documentos de API
const apiDocs = [
  archivosDoc,
  binomialesDoc,
  docsDoc,
  estadisticasDoc,
  fechasDoc,
  fisicasDoc,
  gaussDoc,
  graficosDoc,
  imagenesDoc,
  markovDoc,
  matricesDoc,
  mergeDoc,
  neuralDoc,
  numericosDoc,
  openIaDoc,
  ordenarTablasDoc,
  poissonDoc,
  simplex2phDoc,
  sqliteDoc,
  stringsDoc,
  tablasDoc,
  vectoresDoc,
  websDoc,
];

// Función para convertir nombre de módulo a ID válido
const moduleToId = (moduleName) => {
  return moduleName.replace('.api', '').replace(/_/g, '-');
};

// Función para convertir nombre de función a ID válido
const funcToId = (moduleName, funcName) => {
  const moduleId = moduleToId(moduleName);
  return `${moduleId}-${funcName.replace(/_/g, '-')}`;
};

// Función para generar la firma de la función
const generateSignature = (func) => {
  const args = func.args ? func.args.map(a => a.name).join(', ') : '';
  return `${func.name}(${args})`;
};

// Función para generar el contenido markdown de una función
const generateFunctionContent = (moduleName, func) => {
  const modulePrefix = moduleName.replace('.api', '');
  const args = func.args ? func.args.map(a => a.name).join(', ') : '';
  
  // Convertir \n escapados a saltos de línea reales para markdown
  const description = func.description ? func.description.replace(/\\n/g, '\n') : '';
  
  // Sin título H1 aquí - el título viene de DocsPage.jsx
  // Descripción directa
  let content = `${description}\n\n`;
  
  // Parámetros
  if (func.args && func.args.length > 0) {
    content += `## Parámetros\n\n`;
    content += `| Parámetro | Tipo | Descripción |\n`;
    content += `|-----------|------|-------------|\n`;
    func.args.forEach(arg => {
      content += `| ${arg.name} | ${arg.type} | ${arg.description} |\n`;
    });
    content += `\n`;
  }
  
  // Valor de retorno
  if (func.returns) {
    content += `## Valor de retorno\n\n`;
    content += `**${func.returns.type}** - ${func.returns.description}\n\n`;
  }
  
  // Ejemplo - usar el ejemplo real si existe, sino generar uno básico
  content += `## Ejemplo\n\n`;
  content += `\`\`\`blox\n`;
  
  if (func.example) {
    // Usar ejemplo real del archivo
    content += func.example;
  } else {
    // Generar ejemplo básico
    if (func.returns && func.returns.type !== 'VOID') {
      content += `${func.returns.type} resultado\n`;
    }
    const args = func.args ? func.args.map(a => a.name).join(', ') : '';
    if (func.returns && func.returns.type !== 'VOID') {
      content += `resultado = ${modulePrefix}.${func.name}(${args})\n`;
    } else {
      content += `${modulePrefix}.${func.name}(${args})\n`;
    }
  }
  
  content += `\`\`\``;
  
  return content;
};

// Generar sidebar items para las APIs desde JSON
export const generateApiSidebarItems = () => {
  return apiDocs.map(doc => ({
    id: `api-${moduleToId(doc.module)}`,
    title: `API: ${doc.module}`,
    items: doc.functions.map(func => ({
      id: funcToId(doc.module, func.name),
      title: `${func.name}()`,
      path: `/docs/api-${moduleToId(doc.module)}/${func.name.replace(/_/g, '-')}`
    }))
  }));
};

// Generar contenido de documentación para las APIs desde JSON
export const generateApiDocsContent = () => {
  const content = {};
  
  apiDocs.forEach(doc => {
    const modulePrefix = doc.module.replace('.api', '');
    doc.functions.forEach(func => {
      const id = funcToId(doc.module, func.name);
      const args = func.args ? func.args.map(a => a.name).join(', ') : '';
      content[id] = {
        title: `${func.name}(${args})`,
        titleLatex: func.title_latex || null,
        playgroundAccess: func.playground_access || doc.playground_access || 'public',
        content: generateFunctionContent(doc.module, func)
      };
    });
  });
  
  return content;
};

// Exportar los documentos crudos por si se necesitan
export const rawApiDocs = apiDocs;

// Descripciones cortas de cada módulo API (editables)
const API_DESCRIPTIONS = {
  'archivos.api': 'Operaciones avanzadas con archivos',
  'binomiales.api': 'Distribución binomial y combinatorias',
  'docs.api': 'Lectura de la documentación interna de BLOX',
  'estadisticas.api': 'Medidas estadísticas y regresiones',
  'fechas.api': 'Fechas, calendario y días de la semana',
  'fisicas.api': 'Simulaciones físicas (RK4, resortes)',
  'gauss.api': 'Sistemas lineales (Gauss, Gauss-Seidel)',
  'graficos.api': 'Gráficos cartesianos, dispersiones e histogramas',
  'imagenes.api': 'Lectura y escritura de imágenes PGM',
  'markov.api': 'Cadenas de Markov y probabilidades de transición',
  'matrices.api': 'Operaciones con matrices (inversa, determinante...)',
  'merge.api': 'Mezcla y deduplicación',
  'neural.api': 'Redes neuronales básicas',
  'numericos.api': 'Métodos numéricos (Newton-Raphson...)',
  'open_ia.api': 'Integración con OpenAI',
  'ordenar_tablas.api': 'Ordenamiento de tablas',
  'poisson.api': 'Distribución de Poisson',
  'simplex2ph.api': 'Programación lineal (Simplex dos fases)',
  'sqlite.api': 'Bases de datos SQLite embebidas',
  'strings.api': 'Manipulación avanzada de cadenas',
  'tablas.api': 'Operaciones con tablas y registros',
  'vectores.api': 'Algoritmos con vectores',
  'webs.api': 'Requests HTTP y parseo JSON'
};

// Resumen de módulos API (para la página hub)
export const getApiModulesSummary = () => {
  return apiDocs.map(doc => {
    const firstFn = doc.functions[0];
    const firstPath = firstFn
      ? `/docs/api-${moduleToId(doc.module)}/${firstFn.name.replace(/_/g, '-')}`
      : '/docs';
    return {
      id: `api-${moduleToId(doc.module)}`,
      name: doc.module,
      count: doc.functions.length,
      description: API_DESCRIPTIONS[doc.module] || '',
      firstPath,
    };
  });
};

// =====================================================
// FUNCIONES INTERNAS
// =====================================================

import funcionesInternasDoc from './docs/funciones_internas.json';

// Mapeo de grupos a IDs amigables
const groupToId = (group) => {
  const map = {
    'NUMERICAS': 'internas-numericas',
    'MATEMATICAS': 'internas-matematicas',
    'STRINGS': 'internas-strings',
    'TABLAS': 'internas-tablas',
    'MATRICES': 'internas-matrices',
    'ARCHIVOS': 'internas-archivos',
    'IO': 'internas-io',
    'TECLADO': 'internas-teclado',
    'SOCKETS_Y_REDES': 'internas-sockets',
    'GRAFICOS': 'internas-graficos',
    'DEPURACION': 'internas-depuracion',
    'EVAL': 'internas-eval',
    'WEB_IA': 'internas-web-ia',
    'CONVERSION': 'internas-conversion',
    'UTIL': 'internas-util',
    'SQLITE': 'internas-sqlite'
  };
  return map[group] || `internas-${group.toLowerCase()}`;
};

// Mapeo de grupos a títulos legibles
const groupToTitle = (group) => {
  const map = {
    'NUMERICAS': 'Funciones Numéricas',
    'MATEMATICAS': 'Funciones Matemáticas',
    'STRINGS': 'Funciones de Strings',
    'TABLAS': 'Funciones de Tablas',
    'MATRICES': 'Funciones de Matrices',
    'ARCHIVOS': 'Funciones de Archivos',
    'IO': 'Entrada/Salida',
    'TECLADO': 'Teclado',
    'SOCKETS_Y_REDES': 'Sockets y Redes',
    'GRAFICOS': 'Gráficos',
    'DEPURACION': 'Depuración',
    'EVAL': 'Evaluación',
    'WEB_IA': 'Web e IA',
    'CONVERSION': 'Conversión de Tipos',
    'UTIL': 'Utilidades',
    'SQLITE': 'SQLite'
  };
  return map[group] || group;
};

// Generar contenido para función interna
const generateInternalFunctionContent = (func) => {
  const description = func.description ? func.description.replace(/\\n/g, '\n') : '';
  const args = func.args ? func.args.map(a => a.name).join(', ') : '';
  
  // Sin título H1 aquí - el título viene de DocsPage.jsx
  // Descripción directa
  let content = `${description}\n\n`;
  
  if (func.args && func.args.length > 0) {
    content += `## Parámetros\n\n`;
    content += `| Parámetro | Tipo | Descripción |\n`;
    content += `|-----------|------|-------------|\n`;
    func.args.forEach(arg => {
      content += `| ${arg.name} | ${arg.type} | ${arg.description} |\n`;
    });
    content += `\n`;
  }
  
  if (func.returns) {
    content += `## Valor de retorno\n\n`;
    content += `**${func.returns.type}** - ${func.returns.description}\n\n`;
  }
  
  content += `## Ejemplo\n\n`;
  content += `\`\`\`blox\n`;
  
  if (func.example) {
    // El ejemplo ya viene con saltos de línea correctos del archivo original
    // No convertimos \n a saltos reales para preservar los \n literales en strings
    content += func.example;
  } else {
    const args = func.args ? func.args.map(a => a.name).join(', ') : '';
    if (func.returns && func.returns.type !== 'VOID') {
      content += `${func.returns.type} resultado\n`;
      content += `resultado = ${func.name}(${args})\n`;
    } else {
      content += `${func.name}(${args})\n`;
    }
  }
  
  content += `\`\`\``;
  
  return content;
};

// Generar sidebar para funciones internas
export const generateInternalsSidebarItems = () => {
  const functions = funcionesInternasDoc.functions || [];
  
  // Agrupar por grupo
  const grouped = {};
  functions.forEach(func => {
    const group = func.group || 'OTROS';
    if (!grouped[group]) {
      grouped[group] = [];
    }
    grouped[group].push(func);
  });
  
  // Orden de grupos preferido
  const groupOrder = [
    'NUMERICAS', 'MATEMATICAS', 'CONVERSION', 'STRINGS', 'TABLAS', 'MATRICES',
    'ARCHIVOS', 'IO', 'TECLADO', 'SOCKETS_Y_REDES', 'GRAFICOS',
    'DEPURACION', 'EVAL', 'WEB_IA', 'SQLITE', 'UTIL'
  ];
  
  return groupOrder
    .filter(g => grouped[g])
    .map(group => ({
      id: groupToId(group),
      title: groupToTitle(group),
      items: grouped[group].map(func => ({
        id: `int-${func.name.toLowerCase().replace(/_/g, '-')}`,
        title: `${func.name}()`,
        path: `/docs/${groupToId(group)}/${func.name.toLowerCase().replace(/_/g, '-')}`
      }))
    }));
};

// Generar contenido para funciones internas
export const generateInternalsDocsContent = () => {
  const content = {};
  const functions = funcionesInternasDoc.functions || [];
  
  functions.forEach(func => {
    const id = `int-${func.name.toLowerCase().replace(/_/g, '-')}`;
    const args = func.args ? func.args.map(a => a.name).join(', ') : '';
    content[id] = {
      title: `${func.name}(${args})`,
      titleLatex: func.title_latex || null,
      playgroundAccess: func.playground_access || funcionesInternasDoc.playground_access || 'public',
      content: generateInternalFunctionContent(func)
    };
  });
  
  return content;
};

// Descripciones cortas de cada grupo interno (editables)
const INTERNAL_GROUP_DESCRIPTIONS = {
  'NUMERICAS': 'Conversiones, redondeos y aleatorios',
  'MATEMATICAS': 'Trigonometría, potencias y logaritmos',
  'CONVERSION': 'Conversión entre tipos (número, string, hexa, lógico)',
  'STRINGS': 'Concatenar, dividir y comparar cadenas',
  'TABLAS': 'Agregar, eliminar y ordenar registros',
  'MATRICES': 'Operaciones básicas con matrices y vectores',
  'ARCHIVOS': 'Abrir, leer y escribir archivos',
  'IO': 'IMPRIMIR, LEER desde teclado',
  'TECLADO': 'Lectura avanzada de teclado (sin ENTER)',
  'SOCKETS_Y_REDES': 'Comunicación TCP/IP',
  'GRAFICOS': 'Generación de imágenes y gráficos',
  'DEPURACION': 'Herramientas de debugging',
  'EVAL': 'Evaluar expresiones dinámicas',
  'WEB_IA': 'Utilidades web e IA',
  'SQLITE': 'Acceso a bases de datos SQLite',
  'UTIL': 'Utilidades generales'
};

// Resumen de grupos de funciones internas (para la página hub)
export const getInternalGroupsSummary = () => {
  const functions = funcionesInternasDoc.functions || [];
  const grouped = {};
  functions.forEach(func => {
    const group = func.group || 'OTROS';
    if (!grouped[group]) grouped[group] = [];
    grouped[group].push(func);
  });

  const groupOrder = [
    'NUMERICAS', 'MATEMATICAS', 'CONVERSION', 'STRINGS', 'TABLAS', 'MATRICES',
    'ARCHIVOS', 'IO', 'TECLADO', 'SOCKETS_Y_REDES', 'GRAFICOS',
    'DEPURACION', 'EVAL', 'WEB_IA', 'SQLITE', 'UTIL'
  ];

  return groupOrder
    .filter(g => grouped[g])
    .map(group => {
      const firstFn = grouped[group][0];
      return {
        id: groupToId(group),
        name: groupToTitle(group),
        rawGroup: group,
        count: grouped[group].length,
        description: INTERNAL_GROUP_DESCRIPTIONS[group] || '',
        firstPath: `/docs/${groupToId(group)}/${firstFn.name.toLowerCase().replace(/_/g, '-')}`
      };
    });
};

